import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdSearch, MdAdd, MdEdit, MdDelete, MdWork,
  MdCheckCircle, MdPause, MdCancel, MdPeople, MdRefresh, MdUpload,
  MdArrowUpward, MdArrowDownward, MdFileDownload, MdFilterList,
  MdVisibility, MdClose, MdPersonSearch, MdEventNote, MdThumbUp, MdLocalOffer, MdCelebration,
  MdViewColumn, MdVisibilityOff
} from 'react-icons/md';
import PageHeader from '../components/PageHeader';
import SkeletonRow from '../components/SkeletonRow';
import BulkUpload from '../components/BulkUpload';
import ExcelBulkUpload from '../components/ExcelBulkUpload';
import { jobRequisitionApi, jobMasterApi, usersApi, candidateApi, jobBulkUploadApi } from '../../../api/jobManagementApi';
import { locationApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import '../Admin.css';
import './JobRequisitions.css';

const REQUISITION_STATUSES = [
  { value: 'OPEN', label: 'Open', color: 'open' },
  { value: 'ON_HOLD', label: 'On Hold', color: 'on-hold' },
  { value: 'CLOSED', label: 'Closed', color: 'closed' },
  { value: 'FILLED', label: 'Filled', color: 'filled' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'cancelled' }
];

const HIRING_TYPES = [
  { value: 'NEW', label: 'New' },
  { value: 'BACKFILL', label: 'Backfill' }
];

const EXPERIENCE_TYPES = [
  { value: 'FRESHER', label: 'Fresher' },
  { value: 'EXPERIENCE', label: 'Experience' },
  { value: 'BOTH', label: 'Both' }
];

// Column configuration for visibility toggle
const ALL_COLUMNS = [
  { id: 'sno', label: 'S.No', default: true, alwaysVisible: true },
  { id: 'jrCode', label: 'JR Code', default: true, sortKey: 'requisitionCode' },
  { id: 'vertical', label: 'Vertical', default: true },
  { id: 'title', label: 'Title/Designation', default: true, sortKey: 'processName' },
  { id: 'specialty', label: 'Specialty', default: false },
  { id: 'location', label: 'Location', default: true },
  { id: 'positions', label: 'Positions', default: true, sortKey: 'positionsApproved' },
  { id: 'pipeline', label: 'Pipeline', default: true },
  { id: 'hiringManager', label: 'Hiring Manager', default: false },
  { id: 'hrLead', label: 'HR Lead', default: false },
  { id: 'recruiter', label: 'Recruiter', default: true },
  { id: 'approvalDate', label: 'Approval Date', default: true, sortKey: 'approvalDate' },
  { id: 'status', label: 'Status', default: true, sortKey: 'requisitionStatus' },
  { id: 'actions', label: 'Actions', default: true, alwaysVisible: true }
];

// Load saved column preferences from localStorage
const loadColumnPreferences = () => {
  try {
    const saved = localStorage.getItem('jr_column_visibility');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Failed to load column preferences:', e);
  }
  // Return default visibility
  return ALL_COLUMNS.reduce((acc, col) => {
    acc[col.id] = col.default;
    return acc;
  }, {});
};

function JobRequisitions() {
  const navigate = useNavigate();
  const [requisitions, setRequisitions] = useState([]);
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const [filters, setFilters] = useState({
    query: '',
    status: '',
    verticalId: '',
    recruiterId: '',
    locationId: '',
    hiringType: '',
    experienceType: '',
    startDate: '',
    endDate: ''
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [pipelineCounts, setPipelineCounts] = useState({}); // JR ID -> pipeline counts
  const [showJRDetailModal, setShowJRDetailModal] = useState(false);
  const [selectedJRForDetail, setSelectedJRForDetail] = useState(null);
  const [jrCandidates, setJrCandidates] = useState([]);
  const [loadingJRDetail, setLoadingJRDetail] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 15;

  // Sorting
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // Dropdowns
  const [verticals, setVerticals] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [locations, setLocations] = useState([]);
  const [recruiters, setRecruiters] = useState([]);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingRequisition, setEditingRequisition] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showExcelUpload, setShowExcelUpload] = useState(false);
  const [showUploadMenu, setShowUploadMenu] = useState(false);

  // Column visibility
  const [columnVisibility, setColumnVisibility] = useState(loadColumnPreferences);
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const columnMenuRef = useRef(null);

  // Ref to track the initial vertical when editing (to skip first load)
  const initialEditVerticalRef = useRef(null);

  // Form data - comprehensive fields matching Excel upload
  const [formData, setFormData] = useState({
    // Basic Info
    title: '',
    verticalId: '',
    specialtyId: '',
    designationId: '',
    projectId: '',
    costCenterId: '',
    locationId: '',
    revisedLocationId: '',
    recruiterId: '',
    // Positions & Demand
    originalDemand: 1,
    positionsApproved: 1,
    positionsFilled: 0,
    positionsOffered: 0,
    // Hiring Settings
    hiringType: 'NEW',
    experienceType: 'EXPERIENCE',
    minExperience: 0,
    maxExperience: 3,
    minSalary: '',
    maxSalary: '',
    // Important Dates
    approvalDate: '',
    targetDate: '',
    joClosureDate: '',
    // Management Names
    hrLeadName: '',
    hiringManagerName: '',
    buHeadName: '',
    // System Info
    hbPb: '',
    emrSystem: '',
    productLine: '',
    projectInd: '',
    localCostCenterInd: '',
    approvedInWorkday: '',
    // Evergreen & SLA
    isEvergreen: false,
    slaDays: '',
    // Description
    jobDescription: '',
    requirements: '',
    remarks: ''
  });

  // Load data
  const loadRequisitions = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      const params = {
        page,
        size: pageSize,
        sortBy,
        sortDir
      };

      if (filters.query) params.query = filters.query;
      if (filters.status) params.status = filters.status;
      if (filters.verticalId) params.verticalId = filters.verticalId;
      if (filters.recruiterId) params.recruiterId = filters.recruiterId;
      if (filters.locationId) params.locationId = filters.locationId;
      if (filters.hiringType) params.hiringType = filters.hiringType;
      if (filters.experienceType) params.experienceType = filters.experienceType;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await jobRequisitionApi.getAll(params);

      if (response.success) {
        setRequisitions(response.data.content || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load requisitions');
    } finally {
      setIsLoading(false);
    }
  }, [page, filters, sortBy, sortDir]);

  // Load pipeline counts for displayed JRs
  const loadPipelineCounts = useCallback(async (jrIds) => {
    if (!jrIds || jrIds.length === 0) return;

    try {
      // Get pipeline summary for each JR - batch request would be ideal
      const countsMap = {};
      await Promise.all(jrIds.map(async (id) => {
        try {
          const response = await candidateApi.getPipelineSummary(id);
          if (response.success && response.data) {
            const data = response.data;
            countsMap[id] = {
              total: Object.values(data).reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0),
              sourced: data.SOURCED || 0,
              interview: (data.INTERVIEW_SCHEDULED || 0) + (data.INTERVIEW_COMPLETED || 0),
              selected: data.SELECTED || 0,
              offered: (data.OFFER_RELEASED || 0) + (data.OFFER_ACCEPTED || 0),
              joined: data.JOINED || 0
            };
          }
        } catch (err) {
          console.error(`Failed to load pipeline for JR ${id}:`, err);
        }
      }));
      setPipelineCounts(prev => ({ ...prev, ...countsMap }));
    } catch (e) {
      console.error('Failed to load pipeline counts:', e);
    }
  }, []);

  // Load JR detail with all candidates
  const loadJRDetail = async (jr) => {
    setSelectedJRForDetail(jr);
    setShowJRDetailModal(true);
    setLoadingJRDetail(true);
    setJrCandidates([]);

    try {
      const response = await candidateApi.getByRequisition(jr.id, { size: 100 });
      if (response.success) {
        setJrCandidates(response.data.content || response.data || []);
      }
    } catch (e) {
      toast.error('Failed to load candidates for this JR');
    } finally {
      setLoadingJRDetail(false);
    }
  };

  // Export filtered data
  const handleExport = async () => {
    try {
      // Get all data with current filters (no pagination limit)
      const params = { ...filters, page: 0, size: 10000 };
      const response = await jobRequisitionApi.getAll(params);

      if (response.success && response.data.content) {
        const data = response.data.content;

        // Create CSV content
        const headers = ['JR Code', 'Title', 'Vertical', 'Designation', 'Location', 'Positions Approved', 'Positions Filled', 'Status', 'Recruiter', 'Hiring Type', 'Approval Date'];
        const csvContent = [
          headers.join(','),
          ...data.map(r => [
            r.requisitionCode || '',
            `"${(r.processName || r.title || '').replace(/"/g, '""')}"`,
            r.vertical?.verticalName || '',
            r.designation?.designationName || '',
            r.devisedLocation?.locationName || '',
            r.positionsApproved || 0,
            r.positionsFilled || 0,
            r.requisitionStatus || '',
            r.recruiterName || '',
            r.hiringType || '',
            r.approvalDate ? new Date(r.approvalDate).toLocaleDateString() : ''
          ].join(','))
        ].join('\n');

        // Download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `job_requisitions_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        toast.success(`Exported ${data.length} requisitions`);
      }
    } catch (e) {
      toast.error('Failed to export data');
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      query: '',
      status: '',
      verticalId: '',
      recruiterId: '',
      locationId: '',
      hiringType: '',
      experienceType: '',
      startDate: '',
      endDate: ''
    });
    setPage(0);
  };

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  // Column visibility handlers
  const toggleColumnVisibility = (columnId) => {
    const column = ALL_COLUMNS.find(c => c.id === columnId);
    if (column?.alwaysVisible) return;

    setColumnVisibility(prev => {
      const updated = { ...prev, [columnId]: !prev[columnId] };
      localStorage.setItem('jr_column_visibility', JSON.stringify(updated));
      return updated;
    });
  };

  const resetColumnVisibility = () => {
    const defaults = ALL_COLUMNS.reduce((acc, col) => {
      acc[col.id] = col.default;
      return acc;
    }, {});
    setColumnVisibility(defaults);
    localStorage.setItem('jr_column_visibility', JSON.stringify(defaults));
  };

  const showAllColumns = () => {
    const allVisible = ALL_COLUMNS.reduce((acc, col) => {
      acc[col.id] = true;
      return acc;
    }, {});
    setColumnVisibility(allVisible);
    localStorage.setItem('jr_column_visibility', JSON.stringify(allVisible));
  };

  // Count visible columns
  const visibleColumnCount = Object.values(columnVisibility).filter(Boolean).length;

  // Close column menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(0);
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortDir === 'asc' ? <MdArrowUpward className="sort-icon" /> : <MdArrowDownward className="sort-icon" />;
  };

  const loadStats = async () => {
    try {
      const response = await jobRequisitionApi.getDashboardStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (e) {
      console.error('Failed to load stats:', e);
    }
  };

  const loadDropdowns = async () => {
    try {
      const [verticalsRes, designationsRes, costCentersRes, locationsRes, recruitersRes] = await Promise.all([
        jobMasterApi.getVerticalsDropdown(),
        jobMasterApi.getDesignationsDropdown(),
        jobMasterApi.getCostCentersDropdown(),
        locationApi.getAllActiveLocations(),
        usersApi.getRecruiters() // Only users with recruiter/HR roles
      ]);

      setVerticals(verticalsRes.success ? verticalsRes.data : []);
      setDesignations(designationsRes.success ? designationsRes.data : []);
      setCostCenters(costCentersRes.success ? costCentersRes.data : []);
      setLocations(Array.isArray(locationsRes?.data) ? locationsRes.data : locationsRes || []);
      setRecruiters(Array.isArray(recruitersRes?.data) ? recruitersRes.data : []);
    } catch (e) {
      console.error('Failed to load dropdowns:', e);
    }
  };

  // Load specialties and projects when vertical changes
  const loadVerticalDependents = async (verticalId) => {
    if (!verticalId) {
      setSpecialties([]);
      setProjects([]);
      return;
    }

    try {
      const [specialtiesRes, projectsRes] = await Promise.all([
        jobMasterApi.getSpecialtiesDropdown(verticalId),
        jobMasterApi.getProjectsDropdown(verticalId)
      ]);

      setSpecialties(specialtiesRes.success ? specialtiesRes.data : []);
      setProjects(projectsRes.success ? projectsRes.data : []);
    } catch (e) {
      console.error('Failed to load vertical dependents:', e);
    }
  };

  useEffect(() => {
    loadRequisitions();
    loadStats();
    loadDropdowns();
  }, [loadRequisitions]);

  // Load pipeline counts when requisitions are loaded
  useEffect(() => {
    if (requisitions.length > 0) {
      const jrIds = requisitions.map(r => r.id);
      loadPipelineCounts(jrIds);
    }
  }, [requisitions, loadPipelineCounts]);

  useEffect(() => {
    // Load vertical dependents when:
    // 1. Adding a new JR (no editingRequisition) and verticalId is set
    // 2. Editing and user changes vertical to a different value
    if (formData.verticalId) {
      if (!editingRequisition) {
        // Adding new JR - always load
        loadVerticalDependents(formData.verticalId);
      } else if (initialEditVerticalRef.current !== formData.verticalId) {
        // User changed vertical in edit mode - reload dependents and clear selections
        loadVerticalDependents(formData.verticalId);
        setFormData(prev => ({ ...prev, specialtyId: '', projectId: '' }));
        initialEditVerticalRef.current = formData.verticalId;
      }
    }
  }, [formData.verticalId, editingRequisition]);

  // Handlers
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setEditingRequisition(null);
    setFormData({
      // Basic Info
      title: '',
      verticalId: '',
      specialtyId: '',
      designationId: '',
      projectId: '',
      costCenterId: '',
      locationId: '',
      revisedLocationId: '',
      recruiterId: '',
      // Positions & Demand
      originalDemand: 1,
      positionsApproved: 1,
      positionsFilled: 0,
      positionsOffered: 0,
      // Hiring Settings
      hiringType: 'NEW',
      experienceType: 'EXPERIENCE',
      minExperience: 0,
      maxExperience: 3,
      minSalary: '',
      maxSalary: '',
      // Important Dates
      approvalDate: new Date().toISOString().split('T')[0],
      targetDate: '',
      joClosureDate: '',
      // Management Names
      hrLeadName: '',
      hiringManagerName: '',
      buHeadName: '',
      // System Info
      hbPb: '',
      emrSystem: '',
      productLine: '',
      projectInd: '',
      localCostCenterInd: '',
      approvedInWorkday: '',
      // Evergreen & SLA
      isEvergreen: false,
      slaDays: '',
      // Description
      jobDescription: '',
      requirements: '',
      remarks: ''
    });
    setShowModal(true);
  };

  const openEditModal = async (req) => {
    console.log('Opening edit modal with req:', req);
    console.log('Specialty from req:', req.specialty);
    console.log('Project from req:', req.project);
    setEditingRequisition(req);

    // Set the initial vertical ref to prevent useEffect from reloading and overwriting
    initialEditVerticalRef.current = req.vertical?.id || null;

    // Load dependent dropdowns first if vertical is set
    let loadedSpecialties = [];
    let loadedProjects = [];
    if (req.vertical?.id) {
      try {
        const [specialtiesRes, projectsRes] = await Promise.all([
          jobMasterApi.getSpecialtiesDropdown(req.vertical.id),
          jobMasterApi.getProjectsDropdown(req.vertical.id)
        ]);
        loadedSpecialties = specialtiesRes.success ? specialtiesRes.data : [];
        loadedProjects = projectsRes.success ? projectsRes.data : [];

        // If the current specialty is not in the loaded list, add it
        if (req.specialty?.id && !loadedSpecialties.find(s => s.id === req.specialty.id)) {
          loadedSpecialties.push({
            id: req.specialty.id,
            name: req.specialty.specialtyName || req.specialty.name,
            specialtyName: req.specialty.specialtyName || req.specialty.name
          });
        }

        // If the current project is not in the loaded list, add it
        if (req.project?.id && !loadedProjects.find(p => p.id === req.project.id)) {
          loadedProjects.push({
            id: req.project.id,
            name: req.project.projectName || req.project.name,
            projectName: req.project.projectName || req.project.name
          });
        }

        // If no project entity but projectInd string exists, try to find matching project by name
        if (!req.project && req.projectInd) {
          const matchingProject = loadedProjects.find(p =>
            (p.projectName || p.name)?.toLowerCase() === req.projectInd.toLowerCase()
          );
          if (matchingProject) {
            // Attach the found project so formData can use it
            req._matchedProjectId = matchingProject.id;
            console.log('Matched project by name:', req.projectInd, '->', matchingProject.id);
          }
        }

        setSpecialties(loadedSpecialties);
        setProjects(loadedProjects);
        console.log('Loaded specialties:', loadedSpecialties);
        console.log('Loaded projects:', loadedProjects);
      } catch (e) {
        console.error('Failed to load vertical dependents:', e);
      }
    }

    const newFormData = {
      // Basic Info
      title: req.processName || req.designation?.name || req.title || '',
      verticalId: req.vertical?.id || '',
      specialtyId: req.specialty?.id || '',
      designationId: req.designation?.id || '',
      projectId: req.project?.id || req._matchedProjectId || '',
      costCenterId: req.costCenter?.id || '',
      locationId: req.devisedLocation?.id || '',
      revisedLocationId: req.revisedLocation?.id || '',
      recruiterId: req.recruiterId || '',
      // Positions & Demand
      originalDemand: req.originalDemand || 1,
      positionsApproved: req.positionsApproved || 1,
      positionsFilled: req.positionsFilled || 0,
      positionsOffered: req.positionsOffered || 0,
      // Hiring Settings
      hiringType: req.hiringType || 'NEW',
      experienceType: req.experienceType || 'EXPERIENCE',
      minExperience: req.minExperience || 0,
      maxExperience: req.maxExperience || 3,
      minSalary: req.minSalary || '',
      maxSalary: req.maxSalary || '',
      // Important Dates
      approvalDate: req.approvalDate ? req.approvalDate.split('T')[0] : '',
      targetDate: req.targetDate ? req.targetDate.split('T')[0] : '',
      joClosureDate: req.joClosureDate ? req.joClosureDate.split('T')[0] : '',
      // Management Names
      hrLeadName: req.hrLeadName || '',
      hiringManagerName: req.hiringManagerName || '',
      buHeadName: req.buHeadName || '',
      // System Info
      hbPb: req.hbPb || '',
      emrSystem: req.emrSystem || '',
      productLine: req.productLine || '',
      projectInd: req.projectInd || '',
      localCostCenterInd: req.localCostCenterInd || '',
      approvedInWorkday: req.approvedInWorkday || '',
      // Evergreen & SLA
      isEvergreen: req.isEvergreen || false,
      slaDays: req.slaDays || '',
      // Description
      jobDescription: req.jobDescription || '',
      requirements: req.requirements || '',
      remarks: req.remarks || ''
    };
    console.log('Setting form data to:', newFormData);
    setFormData(newFormData);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRequisition(null);
    setError('');
    initialEditVerticalRef.current = null; // Reset ref when closing modal
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        // Basic Info
        processName: formData.title,
        vertical: formData.verticalId ? { id: formData.verticalId } : null,
        specialty: formData.specialtyId ? { id: formData.specialtyId } : null,
        designation: formData.designationId ? { id: formData.designationId } : null,
        project: formData.projectId ? { id: formData.projectId } : null,
        costCenter: formData.costCenterId ? { id: formData.costCenterId } : null,
        devisedLocation: formData.locationId ? { id: formData.locationId } : null,
        revisedLocation: formData.revisedLocationId ? { id: formData.revisedLocationId } : null,
        recruiterId: formData.recruiterId || null,
        recruiterName: formData.recruiterId ? recruiters.find(r => r.id === formData.recruiterId)?.name : null,
        // Positions & Demand
        originalDemand: parseInt(formData.originalDemand) || 1,
        positionsApproved: parseInt(formData.positionsApproved) || 1,
        positionsFilled: parseInt(formData.positionsFilled) || 0,
        positionsOffered: parseInt(formData.positionsOffered) || 0,
        // Hiring Settings
        hiringType: formData.hiringType,
        experienceType: formData.experienceType,
        minExperience: parseFloat(formData.minExperience) || 0,
        maxExperience: parseFloat(formData.maxExperience) || 0,
        minSalary: formData.minSalary ? parseFloat(formData.minSalary) : null,
        maxSalary: formData.maxSalary ? parseFloat(formData.maxSalary) : null,
        // Important Dates
        approvalDate: formData.approvalDate || null,
        targetDate: formData.targetDate || null,
        joClosureDate: formData.joClosureDate || null,
        // Management Names
        hrLeadName: formData.hrLeadName || null,
        hiringManagerName: formData.hiringManagerName || null,
        buHeadName: formData.buHeadName || null,
        // System Info
        hbPb: formData.hbPb || null,
        emrSystem: formData.emrSystem || null,
        productLine: formData.productLine || null,
        projectInd: formData.projectInd || null,
        localCostCenterInd: formData.localCostCenterInd || null,
        approvedInWorkday: formData.approvedInWorkday || null,
        // Evergreen & SLA
        isEvergreen: formData.isEvergreen || false,
        slaDays: formData.slaDays ? parseInt(formData.slaDays) : null,
        // Description
        jobDescription: formData.jobDescription,
        requirements: formData.requirements,
        remarks: formData.remarks
      };

      let response;
      if (editingRequisition) {
        response = await jobRequisitionApi.update(editingRequisition.id, payload);
        toast.success('Job Requisition updated successfully');
      } else {
        response = await jobRequisitionApi.create(payload);
        toast.success('Job Requisition created successfully');
      }

      if (response.success) {
        await loadRequisitions();
        await loadStats();
        closeModal();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to save requisition';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await jobRequisitionApi.delete(deleteConfirm.id);
      toast.success('Job Requisition deleted successfully');
      await loadRequisitions();
      await loadStats();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete requisition');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleStatusChange = async (req, newStatus) => {
    try {
      await jobRequisitionApi.updateStatus(req.id, newStatus);
      toast.success(`Status updated to ${newStatus}`);
      await loadRequisitions();
      await loadStats();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update status');
    }
  };

  const viewCandidates = (req) => {
    navigate(`/admin/jobs/candidates?requisitionId=${req.id}`);
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'OPEN': 'open',
      'ON_HOLD': 'on-hold',
      'CLOSED': 'closed',
      'FILLED': 'filled',
      'CANCELLED': 'cancelled'
    };
    return statusMap[status] || 'open';
  };

  return (
    <div className="admin-view job-requisitions-view">
      {/* Compact Header with Filters - At Top */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdWork className="title-icon" />
            Job Requisitions
          </h2>
        </div>
        <div className="header-filters-compact">
          <div className="input search-compact">
            <span className="icon"><MdSearch /></span>
            <input
              placeholder="Search by code, title..."
              value={filters.query}
              onChange={(e) => handleFilterChange('query', e.target.value)}
            />
          </div>
          <select
            className="filter-select-compact"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Status</option>
            {REQUISITION_STATUSES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <select
            className="filter-select-compact"
            value={filters.verticalId}
            onChange={(e) => handleFilterChange('verticalId', e.target.value)}
          >
            <option value="">All Verticals</option>
            {verticals.map(v => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
          <button
            className={`btn-book-compact ${showAdvancedFilters ? 'active' : ''}`}
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            title="Advanced Filters"
          >
            <MdFilterList />
            {activeFilterCount > 2 && <span className="filter-badge">{activeFilterCount - 2}</span>}
          </button>
          <button className="btn-book-compact" onClick={handleExport} title="Export to CSV">
            <MdFileDownload />
          </button>
          <div className="column-visibility-container" ref={columnMenuRef}>
            <button
              className={`btn-book-compact ${showColumnMenu ? 'active' : ''}`}
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              title="Column Visibility"
            >
              <MdViewColumn />
              {visibleColumnCount < ALL_COLUMNS.length && (
                <span className="column-badge">{visibleColumnCount}</span>
              )}
            </button>
            {showColumnMenu && (
              <div className="column-visibility-menu">
                <div className="column-menu-header">
                  <span>Show/Hide Columns</span>
                  <div className="column-menu-actions">
                    <button onClick={showAllColumns} className="column-action-btn">Show All</button>
                    <button onClick={resetColumnVisibility} className="column-action-btn">Reset</button>
                  </div>
                </div>
                <div className="column-menu-list">
                  {ALL_COLUMNS.map(col => (
                    <label
                      key={col.id}
                      className={`column-menu-item ${col.alwaysVisible ? 'disabled' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={columnVisibility[col.id]}
                        onChange={() => toggleColumnVisibility(col.id)}
                        disabled={col.alwaysVisible}
                      />
                      <span className="column-label">{col.label}</span>
                      {col.alwaysVisible && <span className="always-visible-badge">Required</span>}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button className="btn-book-compact" onClick={() => loadRequisitions()} title="Refresh">
            <MdRefresh />
          </button>
          <div className="upload-dropdown-container">
            <button
              className="btn-book-compact"
              onClick={() => setShowUploadMenu(!showUploadMenu)}
              title="Bulk Upload"
            >
              <MdUpload />
            </button>
            {showUploadMenu && (
              <div className="upload-dropdown-menu">
                <button
                  className="upload-dropdown-item excel-item"
                  onClick={() => { setShowExcelUpload(true); setShowUploadMenu(false); }}
                >
                  <span className="upload-icon excel">xlsx</span>
                  Upload Excel (JR + Candidates)
                </button>
                <button
                  className="upload-dropdown-item csv-item"
                  onClick={() => { setShowBulkUpload(true); setShowUploadMenu(false); }}
                >
                  <span className="upload-icon csv">csv</span>
                  Upload CSV (JR Only)
                </button>
              </div>
            )}
          </div>
          <button className="btn-book-compact" onClick={openAddModal}>
            <MdAdd />
            <span>Add JR</span>
          </button>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div className="advanced-filters-panel">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Location</label>
              <select
                value={filters.locationId}
                onChange={(e) => handleFilterChange('locationId', e.target.value)}
              >
                <option value="">All Locations</option>
                {locations.map(l => (
                  <option key={l.id} value={l.id}>{l.locationName || l.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Recruiter</label>
              <select
                value={filters.recruiterId}
                onChange={(e) => handleFilterChange('recruiterId', e.target.value)}
              >
                <option value="">All Recruiters</option>
                {recruiters.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Hiring Type</label>
              <select
                value={filters.hiringType}
                onChange={(e) => handleFilterChange('hiringType', e.target.value)}
              >
                <option value="">All Types</option>
                {HIRING_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Experience Type</label>
              <select
                value={filters.experienceType}
                onChange={(e) => handleFilterChange('experienceType', e.target.value)}
              >
                <option value="">All</option>
                {EXPERIENCE_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>From Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>To Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn-clear-filters" onClick={clearFilters}>
              <MdClose /> Clear All Filters
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-icon open"><MdWork /></div>
          <div className="stat-info">
            <h4>Open Positions</h4>
            <span className="stat-value">{stats.openRequisitions || 0}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon filled"><MdCheckCircle /></div>
          <div className="stat-info">
            <h4>Filled</h4>
            <span className="stat-value">{stats.filledRequisitions || 0}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon hold"><MdPause /></div>
          <div className="stat-info">
            <h4>On Hold</h4>
            <span className="stat-value">{stats.onHoldRequisitions || 0}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon closed"><MdCancel /></div>
          <div className="stat-info">
            <h4>Closed</h4>
            <span className="stat-value">{stats.closedRequisitions || 0}</span>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Table */}
      <div className="table-container">
        {isLoading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading requisitions...</p>
          </div>
        ) : requisitions.length === 0 ? (
          <div className="empty-state">
            <h3>No Job Requisitions Found</h3>
            <p>Create a new job requisition to get started</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                {columnVisibility.sno && <th>S.No</th>}
                {columnVisibility.jrCode && (
                  <th className="sortable" onClick={() => handleSort('requisitionCode')}>
                    JR Code {renderSortIcon('requisitionCode')}
                  </th>
                )}
                {columnVisibility.vertical && <th>Vertical</th>}
                {columnVisibility.title && (
                  <th className="sortable" onClick={() => handleSort('processName')}>
                    Title/Designation {renderSortIcon('processName')}
                  </th>
                )}
                {columnVisibility.specialty && <th>Specialty</th>}
                {columnVisibility.location && <th>Location</th>}
                {columnVisibility.positions && (
                  <th className="sortable" onClick={() => handleSort('positionsApproved')}>
                    Positions {renderSortIcon('positionsApproved')}
                  </th>
                )}
                {columnVisibility.pipeline && <th>Pipeline</th>}
                {columnVisibility.hiringManager && <th>Hiring Manager</th>}
                {columnVisibility.hrLead && <th>HR Lead</th>}
                {columnVisibility.recruiter && <th>Recruiter</th>}
                {columnVisibility.approvalDate && (
                  <th className="sortable" onClick={() => handleSort('approvalDate')}>
                    Approval Date {renderSortIcon('approvalDate')}
                  </th>
                )}
                {columnVisibility.status && (
                  <th className="sortable" onClick={() => handleSort('requisitionStatus')}>
                    Status {renderSortIcon('requisitionStatus')}
                  </th>
                )}
                {columnVisibility.actions && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {requisitions.map((req, index) => (
                <tr key={req.id}>
                  {columnVisibility.sno && <td className="sno-cell">{page * pageSize + index + 1}</td>}
                  {columnVisibility.jrCode && <td className="code-cell">{req.requisitionCode || '-'}</td>}
                  {columnVisibility.vertical && <td>{req.vertical?.verticalName || '-'}</td>}
                  {columnVisibility.title && <td className="title-cell">{req.processName || req.designation?.name || req.title || '-'}</td>}
                  {columnVisibility.specialty && <td>{req.specialty?.name || '-'}</td>}
                  {columnVisibility.location && <td>{req.devisedLocation?.locationName || '-'}</td>}
                  {columnVisibility.positions && (
                    <td className="positions-cell">
                      <span className="filled">{req.positionsFilled || 0}</span>
                      <span className="separator"> / </span>
                      <span className="approved">{req.positionsApproved || 0}</span>
                    </td>
                  )}
                  {columnVisibility.pipeline && (
                    <td className="pipeline-cell">
                      {pipelineCounts[req.id] ? (
                        <div className="pipeline-mini" onClick={() => loadJRDetail(req)} title="Click to view pipeline details">
                          <span className="pipeline-stage sourced" title={`Sourced: ${pipelineCounts[req.id].sourced}`}>
                            <MdPersonSearch className="stage-icon" />
                            <span className="stage-count">{pipelineCounts[req.id].sourced}</span>
                          </span>
                          <span className="pipeline-stage interview" title={`Interview: ${pipelineCounts[req.id].interview}`}>
                            <MdEventNote className="stage-icon" />
                            <span className="stage-count">{pipelineCounts[req.id].interview}</span>
                          </span>
                          <span className="pipeline-stage selected" title={`Selected: ${pipelineCounts[req.id].selected}`}>
                            <MdThumbUp className="stage-icon" />
                            <span className="stage-count">{pipelineCounts[req.id].selected}</span>
                          </span>
                          <span className="pipeline-stage offered" title={`Offered: ${pipelineCounts[req.id].offered}`}>
                            <MdLocalOffer className="stage-icon" />
                            <span className="stage-count">{pipelineCounts[req.id].offered}</span>
                          </span>
                          <span className="pipeline-stage joined" title={`Joined: ${pipelineCounts[req.id].joined}`}>
                            <MdCelebration className="stage-icon" />
                            <span className="stage-count">{pipelineCounts[req.id].joined}</span>
                          </span>
                        </div>
                      ) : (
                        <span className="pipeline-loading">-</span>
                      )}
                    </td>
                  )}
                  {columnVisibility.hiringManager && <td>{req.hiringManagerName || '-'}</td>}
                  {columnVisibility.hrLead && <td>{req.hrLeadName || '-'}</td>}
                  {columnVisibility.recruiter && (
                    <td>
                      {req.recruiterName || '-'}
                      {(req.assignmentType === 'AUTO_SINGLE' || req.assignmentType === 'AUTO_BULK') && (
                        <span className="auto-assigned-badge" title="Auto-assigned">Auto</span>
                      )}
                    </td>
                  )}
                  {columnVisibility.approvalDate && <td>{req.approvalDate ? new Date(req.approvalDate).toLocaleDateString() : '-'}</td>}
                  {columnVisibility.status && (
                    <td>
                      <span className={`status-badge ${getStatusClass(req.requisitionStatus)}`}>
                        {req.requisitionStatus || 'OPEN'}
                      </span>
                    </td>
                  )}
                  {columnVisibility.actions && (
                    <td className="actions-cell">
                      <button className="btn-action-compact btn-view" onClick={() => loadJRDetail(req)} title="View Pipeline">
                        <MdVisibility />
                      </button>
                      <button className="btn-action-compact btn-candidates" onClick={() => viewCandidates(req)} title="Manage Candidates">
                        <MdPeople />
                      </button>
                      <button className="btn-action-compact btn-edit" onClick={() => openEditModal(req)} title="Edit">
                        <MdEdit />
                      </button>
                      <button className="btn-action-compact btn-delete" onClick={() => setDeleteConfirm(req)} title="Delete">
                        <MdDelete />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, totalElements)} of {totalElements} entries
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }).map((_, idx) => {
              const pageNum = Math.max(0, Math.min(page - 2, totalPages - 5)) + idx;
              if (pageNum >= totalPages) return null;
              return (
                <button
                  key={pageNum}
                  className={`pagination-btn ${page === pageNum ? 'active' : ''}`}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            <button
              className="pagination-btn"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h3>{editingRequisition ? 'Edit Job Requisition' : 'Add Job Requisition'}</h3>
              <button className="modal-close" onClick={closeModal}>x</button>
            </div>

            <form onSubmit={handleSubmit} className="user-form edit-form-sections">
              {/* Basic Information Section */}
              <div className="form-section">
                <h4 className="form-section-title">Basic Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="title">Job Title *</label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Senior Medical Coder"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="verticalId">Vertical/Segment *</label>
                    <select
                      id="verticalId"
                      name="verticalId"
                      value={formData.verticalId}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Vertical</option>
                      {verticals.map(v => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="specialtyId">Specialty</label>
                    <select
                      id="specialtyId"
                      name="specialtyId"
                      value={formData.specialtyId}
                      onChange={handleInputChange}
                      disabled={!formData.verticalId}
                    >
                      <option value="">Select Specialty</option>
                      {specialties.map(s => (
                        <option key={s.id} value={s.id}>{s.specialtyName || s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="designationId">Designation *</label>
                    <select
                      id="designationId"
                      name="designationId"
                      value={formData.designationId}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Designation</option>
                      {designations.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Location & Assignment Section */}
              <div className="form-section">
                <h4 className="form-section-title">Location & Assignment</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="locationId">Location *</label>
                    <select
                      id="locationId"
                      name="locationId"
                      value={formData.locationId}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Location</option>
                      {locations.map(l => (
                        <option key={l.id} value={l.id}>{l.locationName || l.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="revisedLocationId">Revised Location</label>
                    <select
                      id="revisedLocationId"
                      name="revisedLocationId"
                      value={formData.revisedLocationId}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Revised Location</option>
                      {locations.map(l => (
                        <option key={l.id} value={l.id}>{l.locationName || l.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="projectId">Project/Client</label>
                    <select
                      id="projectId"
                      name="projectId"
                      value={formData.projectId}
                      onChange={handleInputChange}
                      disabled={!formData.verticalId}
                    >
                      <option value="">Select Project</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.projectName || p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="costCenterId">Cost Center</label>
                    <select
                      id="costCenterId"
                      name="costCenterId"
                      value={formData.costCenterId}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Cost Center</option>
                      {costCenters.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="recruiterId">Assigned Recruiter</label>
                    <select
                      id="recruiterId"
                      name="recruiterId"
                      value={formData.recruiterId}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Recruiter</option>
                      {recruiters.map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Positions & Demand Section */}
              <div className="form-section">
                <h4 className="form-section-title">Positions & Demand</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="originalDemand">Original Demand</label>
                    <input type="number" id="originalDemand" name="originalDemand" value={formData.originalDemand} onChange={handleInputChange} min="0" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="positionsApproved">Positions Approved *</label>
                    <input type="number" id="positionsApproved" name="positionsApproved" value={formData.positionsApproved} onChange={handleInputChange} min="1" required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="positionsFilled">Positions Filled</label>
                    <input type="number" id="positionsFilled" name="positionsFilled" value={formData.positionsFilled} onChange={handleInputChange} min="0" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="positionsOffered">Positions Offered</label>
                    <input type="number" id="positionsOffered" name="positionsOffered" value={formData.positionsOffered} onChange={handleInputChange} min="0" />
                  </div>
                </div>
              </div>

              {/* Hiring Settings Section */}
              <div className="form-section">
                <h4 className="form-section-title">Hiring Settings</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="hiringType">Hiring Type *</label>
                    <select id="hiringType" name="hiringType" value={formData.hiringType} onChange={handleInputChange} required>
                      {HIRING_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="experienceType">Experience Type *</label>
                    <select id="experienceType" name="experienceType" value={formData.experienceType} onChange={handleInputChange} required>
                      {EXPERIENCE_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="isEvergreen">Evergreen</label>
                    <select id="isEvergreen" name="isEvergreen" value={formData.isEvergreen} onChange={(e) => setFormData(prev => ({ ...prev, isEvergreen: e.target.value === 'true' }))}>
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="slaDays">SLA (Days)</label>
                    <input type="number" id="slaDays" name="slaDays" value={formData.slaDays} onChange={handleInputChange} min="0" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="minExperience">Min Experience (Years)</label>
                    <input type="number" id="minExperience" name="minExperience" value={formData.minExperience} onChange={handleInputChange} min="0" step="0.5" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="maxExperience">Max Experience (Years)</label>
                    <input type="number" id="maxExperience" name="maxExperience" value={formData.maxExperience} onChange={handleInputChange} min="0" step="0.5" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="minSalary">Min Salary (CTC)</label>
                    <input type="number" id="minSalary" name="minSalary" value={formData.minSalary} onChange={handleInputChange} placeholder="e.g., 500000" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="maxSalary">Max Salary (CTC)</label>
                    <input type="number" id="maxSalary" name="maxSalary" value={formData.maxSalary} onChange={handleInputChange} placeholder="e.g., 800000" />
                  </div>
                </div>
              </div>

              {/* Important Dates Section */}
              <div className="form-section">
                <h4 className="form-section-title">Important Dates</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="approvalDate">Approval Date</label>
                    <input type="date" id="approvalDate" name="approvalDate" value={formData.approvalDate} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="targetDate">Target Closure Date</label>
                    <input type="date" id="targetDate" name="targetDate" value={formData.targetDate} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="joClosureDate">JO Closure Date</label>
                    <input type="date" id="joClosureDate" name="joClosureDate" value={formData.joClosureDate} onChange={handleInputChange} />
                  </div>
                </div>
              </div>

              {/* Management & Stakeholders Section */}
              <div className="form-section">
                <h4 className="form-section-title">Management & Stakeholders</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="hrLeadName">HR Lead</label>
                    <input type="text" id="hrLeadName" name="hrLeadName" value={formData.hrLeadName} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="hiringManagerName">Hiring Manager</label>
                    <input type="text" id="hiringManagerName" name="hiringManagerName" value={formData.hiringManagerName} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="buHeadName">BU Head</label>
                    <input type="text" id="buHeadName" name="buHeadName" value={formData.buHeadName} onChange={handleInputChange} />
                  </div>
                </div>
              </div>

              {/* System Information Section */}
              <div className="form-section">
                <h4 className="form-section-title">System Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="hbPb">H.B./P.B</label>
                    <input type="text" id="hbPb" name="hbPb" value={formData.hbPb} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="emrSystem">EMR System</label>
                    <input type="text" id="emrSystem" name="emrSystem" value={formData.emrSystem} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="productLine">Product Line</label>
                    <input type="text" id="productLine" name="productLine" value={formData.productLine} onChange={handleInputChange} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="projectInd">Project (IND)</label>
                    <input type="text" id="projectInd" name="projectInd" value={formData.projectInd} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="localCostCenterInd">Local Cost Center (IND)</label>
                    <input type="text" id="localCostCenterInd" name="localCostCenterInd" value={formData.localCostCenterInd} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="approvedInWorkday">Approved in Workday</label>
                    <input type="text" id="approvedInWorkday" name="approvedInWorkday" value={formData.approvedInWorkday} onChange={handleInputChange} />
                  </div>
                </div>
              </div>

              {/* Description Section */}
              <div className="form-section">
                <h4 className="form-section-title">Description & Notes</h4>
                <div className="form-row">
                  <div className="form-group" style={{ flex: '1 1 100%' }}>
                    <label htmlFor="jobDescription">Job Description</label>
                    <textarea id="jobDescription" name="jobDescription" value={formData.jobDescription} onChange={handleInputChange} rows="3" placeholder="Enter job description..." />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ flex: '1 1 100%' }}>
                    <label htmlFor="requirements">Requirements</label>
                    <textarea id="requirements" name="requirements" value={formData.requirements} onChange={handleInputChange} rows="3" placeholder="Enter requirements..." />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ flex: '1 1 100%' }}>
                    <label htmlFor="remarks">Remarks</label>
                    <textarea id="remarks" name="remarks" value={formData.remarks} onChange={handleInputChange} rows="2" placeholder="Any additional remarks..." />
                  </div>
                </div>
              </div>

              {error && (
                <div className="error-message" style={{ color: 'red', marginBottom: '15px', padding: '10px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
                  {error}
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : (editingRequisition ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>x</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete requisition <strong>{deleteConfirm.requisitionCode}</strong>?</p>
              <p style={{ color: '#dc2626', fontSize: '13px' }}>This action cannot be undone.</p>
            </div>
            <div className="form-actions">
              <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button type="button" className="admin-btn admin-btn-danger" onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <BulkUpload
          title="Bulk Upload Job Requisitions"
          entityName="Job Requisitions"
          templateColumns={[
            { name: 'title', label: 'Job Title', required: true, type: 'string', example: 'Senior Medical Coder' },
            { name: 'verticalName', label: 'Vertical/Segment', required: true, type: 'string', example: 'Medical Coding', dropdown: 'verticals' },
            { name: 'designationName', label: 'Designation', required: true, type: 'string', example: 'Senior Coder', dropdown: 'designations' },
            { name: 'locationName', label: 'Location', required: true, type: 'string', example: 'Hyderabad', dropdown: 'locations' },
            { name: 'positionsApproved', label: 'Positions Approved', required: true, type: 'number', example: '5' },
            { name: 'hiringType', label: 'Hiring Type', required: false, type: 'string', example: 'NEW' },
            { name: 'experienceType', label: 'Experience Type', required: false, type: 'string', example: 'EXPERIENCE' },
            { name: 'minExperience', label: 'Min Experience (Years)', required: false, type: 'number', example: '2' },
            { name: 'maxExperience', label: 'Max Experience (Years)', required: false, type: 'number', example: '5' },
            { name: 'minSalary', label: 'Min Salary', required: false, type: 'number', example: '500000' },
            { name: 'maxSalary', label: 'Max Salary', required: false, type: 'number', example: '800000' },
            { name: 'targetDate', label: 'Target Date', required: false, type: 'date', example: '2025-03-31' },
            { name: 'jobDescription', label: 'Job Description', required: false, type: 'string', example: 'Job description here' }
          ]}
          dropdownData={{
            verticals: verticals,
            designations: designations,
            locations: locations
          }}
          onUpload={async (data) => {
            // Transform data to match API format
            const transformed = data.map(row => ({
              processName: row.title,
              vertical: verticals.find(v => v.name?.toLowerCase() === row.verticalName?.toLowerCase()) || null,
              designation: designations.find(d => d.name?.toLowerCase() === row.designationName?.toLowerCase()) || null,
              devisedLocation: locations.find(l => (l.locationName || l.name)?.toLowerCase() === row.locationName?.toLowerCase()) || null,
              positionsApproved: parseInt(row.positionsApproved) || 1,
              originalDemand: parseInt(row.positionsApproved) || 1,
              hiringType: row.hiringType || 'NEW',
              experienceType: row.experienceType || 'EXPERIENCE',
              minExperience: parseFloat(row.minExperience) || 0,
              maxExperience: parseFloat(row.maxExperience) || 3,
              minSalary: row.minSalary ? parseFloat(row.minSalary) : null,
              maxSalary: row.maxSalary ? parseFloat(row.maxSalary) : null,
              targetDate: row.targetDate || null,
              jobDescription: row.jobDescription || ''
            }));

            const response = await jobRequisitionApi.bulkCreate(transformed);
            if (response.success) {
              await loadRequisitions();
              await loadStats();
              toast.success(`Successfully uploaded ${transformed.length} requisitions`);
            }
            return response;
          }}
          onClose={() => setShowBulkUpload(false)}
        />
      )}

      {/* Excel Bulk Upload Modal */}
      {showExcelUpload && (
        <ExcelBulkUpload
          title="Bulk Upload from Excel"
          onUpload={async (file) => {
            const response = await jobBulkUploadApi.uploadExcel(file);
            if (response.success) {
              await loadRequisitions();
              await loadStats();
              toast.success(response.message || 'Excel data uploaded successfully');
            }
            return response;
          }}
          onClose={() => setShowExcelUpload(false)}
        />
      )}

      {/* JR Pipeline Detail Modal */}
      {showJRDetailModal && selectedJRForDetail && (
        <div className="modal-overlay">
          <div className="modal-content jr-detail-modal" style={{ maxWidth: '1000px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h3>
                Pipeline: {selectedJRForDetail.requisitionCode} - {selectedJRForDetail.processName}
              </h3>
              <button className="modal-close" onClick={() => setShowJRDetailModal(false)}>x</button>
            </div>

            <div className="jr-detail-info">
              <div className="jr-info-row">
                <span><strong>Vertical:</strong> {selectedJRForDetail.vertical?.verticalName || '-'}</span>
                <span><strong>Location:</strong> {selectedJRForDetail.devisedLocation?.locationName || '-'}</span>
                <span><strong>Recruiter:</strong> {selectedJRForDetail.recruiterName || '-'}</span>
                <span><strong>Status:</strong> <span className={`status-badge ${getStatusClass(selectedJRForDetail.requisitionStatus)}`}>{selectedJRForDetail.requisitionStatus}</span></span>
              </div>
              <div className="jr-info-row">
                <span><strong>Positions:</strong> {selectedJRForDetail.positionsFilled || 0} / {selectedJRForDetail.positionsApproved || 0}</span>
                <span><strong>Hiring Type:</strong> {selectedJRForDetail.hiringType || '-'}</span>
                <span><strong>Experience:</strong> {selectedJRForDetail.minExperience || 0} - {selectedJRForDetail.maxExperience || 0} yrs</span>
              </div>
            </div>

            {/* Pipeline Summary */}
            {pipelineCounts[selectedJRForDetail.id] && (
              <div className="jr-pipeline-summary">
                <div className="pipeline-stage-box sourced">
                  <MdPersonSearch className="stage-icon-lg" />
                  <span className="stage-count">{pipelineCounts[selectedJRForDetail.id].sourced}</span>
                  <span className="stage-label">Sourced</span>
                </div>
                <div className="pipeline-stage-box interview">
                  <MdEventNote className="stage-icon-lg" />
                  <span className="stage-count">{pipelineCounts[selectedJRForDetail.id].interview}</span>
                  <span className="stage-label">Interview</span>
                </div>
                <div className="pipeline-stage-box selected">
                  <MdThumbUp className="stage-icon-lg" />
                  <span className="stage-count">{pipelineCounts[selectedJRForDetail.id].selected}</span>
                  <span className="stage-label">Selected</span>
                </div>
                <div className="pipeline-stage-box offered">
                  <MdLocalOffer className="stage-icon-lg" />
                  <span className="stage-count">{pipelineCounts[selectedJRForDetail.id].offered}</span>
                  <span className="stage-label">Offered</span>
                </div>
                <div className="pipeline-stage-box joined">
                  <MdCelebration className="stage-icon-lg" />
                  <span className="stage-count">{pipelineCounts[selectedJRForDetail.id].joined}</span>
                  <span className="stage-label">Joined</span>
                </div>
              </div>
            )}

            {/* Candidates Table */}
            <div className="jr-candidates-section">
              <h4>Candidates ({jrCandidates.length})</h4>
              {loadingJRDetail ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading candidates...</p>
                </div>
              ) : jrCandidates.length === 0 ? (
                <div className="empty-state">
                  <p>No candidates in pipeline for this requisition</p>
                </div>
              ) : (
                <table className="data-table compact-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Source</th>
                      <th>Experience</th>
                      <th>Stage</th>
                      <th>Interview Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jrCandidates.map(candidate => (
                      <tr key={candidate.id}>
                        <td>{candidate.fullName || candidate.firstName || '-'}</td>
                        <td>{candidate.email || '-'}</td>
                        <td>{candidate.contactNo || '-'}</td>
                        <td>{candidate.sourceType || '-'}</td>
                        <td>{candidate.totalExperienceMonths ? `${(candidate.totalExperienceMonths / 12).toFixed(1)} yrs` : '-'}</td>
                        <td>
                          <span className={`stage-badge ${getStageClass(candidate.currentStage)}`}>
                            {candidate.currentStage || '-'}
                          </span>
                        </td>
                        <td>{candidate.dateOfInterview ? new Date(candidate.dateOfInterview).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="admin-btn admin-btn-primary"
                onClick={() => viewCandidates(selectedJRForDetail)}
              >
                <MdPeople /> Manage Candidates
              </button>
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                onClick={() => setShowJRDetailModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function for stage styling in the detail modal
const getStageClass = (stage) => {
  const stageMap = {
    'SOURCED': 'sourced',
    'SCREENING': 'screening',
    'INTERVIEW_SCHEDULED': 'interview',
    'INTERVIEW_COMPLETED': 'interview',
    'SELECTED': 'selected',
    'OFFER_INITIATED': 'offered',
    'OFFER_RELEASED': 'offered',
    'OFFER_ACCEPTED': 'offered',
    'DOCUMENTATION': 'offered',
    'JOINED': 'joined',
    'REJECTED': 'rejected',
    'WITHDRAWN': 'withdrawn',
    'OFFER_DECLINED': 'rejected',
    'NO_SHOW': 'rejected'
  };
  return stageMap[stage] || 'sourced';
};

export default JobRequisitions;
